import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { ToeBoardExtra } from "../../../../../http/border/types/toeBoardExtras";
import { useToeBoardExtrasApi } from "../../../../../http/border/useToeBoardExtras";
import { useToeBoardsApi } from "../../../../../http/border/useToeBoards";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface ToeBoardExtraFormBody {
  item: SearchAdminItem | null;
  toeBoardIds: string[];
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface ToeBoardExtraFormProps {
  toeBoardExtra?: ToeBoardExtra;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose: () => void;
}

const ToeBoardExtraForm: FC<ToeBoardExtraFormProps> = ({ toeBoardExtra, isDisabled, isLoading, onClose }) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());

  const itemsApi = useItemsApi();
  const toeBoardExtrasApi = useToeBoardExtrasApi();
  const toeBoardsApi = useToeBoardsApi();
  const toeBoards = toeBoardsApi.useAdminToeBoards({ limit: 100 }, aborter.current.signal);

  const defaultValues = {
    item: toeBoardExtra?.item ?? null,
    toeBoardIds: toeBoardExtra?.toeBoards?.map((x) => x.item.id) ?? [],
    order: toeBoardExtra?.order ?? 50,
    visibility: toeBoardExtra?.visibility ?? "hidden",
  };

  return (
    <Form<ToeBoardExtraFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        setIsSubmitting(true);

        if (toeBoardExtra?.item.id) {
          try {
            await toeBoardExtrasApi.updateAdminToeBoardExtra(toeBoardExtra.item.id, {
              toeBoardIds: formData.toeBoardIds,
              order: formData.order!,
              visibility: formData.visibility,
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        } else {
          try {
            await toeBoardExtrasApi.insertAdminToeBoardExtra({
              itemId: formData.item?.id ?? "",
              toeBoardIds: formData.toeBoardIds,
              order: formData.order!,
              visibility: formData.visibility,
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      {({ control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [toeBoardExtra]);

        const toeBoardIds = watch("toeBoardIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultOptions}
                        isDisabled={!!toeBoardExtra || isDisabled || isLoading || isSubmitting}
                        loadOptions={loadOptions}
                        isClearable
                        getOptionLabel={itemLabel}
                        getOptionValue={(x) => x.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("item", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.toeBoards.plural}>
              <MultiCheckbox
                disabled={isDisabled || isLoading || isSubmitting}
                values={
                  toeBoards.data?.items.map((x) => ({
                    id: x.item.id,
                    title: itemLabel(x.item),
                    selected: toeBoardIds.includes(x.item.id),
                  })) ?? []
                }
                onUpdate={(id, checked) => {
                  setValue("toeBoardIds", checked ? [...toeBoardIds, id] : toeBoardIds.filter((x) => x !== id));
                }}
              />
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default ToeBoardExtraForm;
